import { useCallback, useEffect, useState } from "react";
import { useAuth, useUser } from "@clerk/clerk-react";
import {
  ApiCallError,
  fetchTiers,
  listKeys,
  registerKey,
  revokeKey,
  startCheckout,
  startPortal,
  type AccountKey,
  type AccountKeyKind,
  type BillingTier,
  type BillingTiersResponse,
} from "./api";
import { ApiKeysPanel } from "./ApiKeysPanel";

// MVP dashboard surface — billing only.
//
// Source of truth for tier display: Clerk publicMetadata.tier (the Stripe
// webhook PATCHes it on subscription.created/updated/deleted). The API
// enforces tier server-side via the JWT claim; this UI value is for
// display only.
//
// Upgrade priceId comes from GET /v1/billing/tiers at runtime — dashboard
// deploys are no longer coupled to Stripe price rotation.

type BillingAction = "checkout" | "portal";

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export function Dashboard() {
  const { user } = useUser();
  const { getToken } = useAuth();
  const [busy, setBusy] = useState<BillingAction | null>(null);
  const [error, setError] = useState<string | null>(null);

  const tier =
    (user?.publicMetadata as { tier?: string } | undefined)?.tier ?? "free";

  const [tiersData, setTiersData] = useState<BillingTiersResponse | null>(null);
  const [tiersError, setTiersError] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    fetchTiers()
      .then((data) => {
        if (!cancelled) setTiersData(data);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setTiersError(
          err instanceof Error ? err.message : "failed to load tiers",
        );
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // First non-default tier with at least one priceId is the upgrade target.
  // Today there's exactly one — Personal. When TIERS_CONFIG grows past one
  // paid tier (Team, Scale, …), this single-button shape stops working —
  // replace with a pricing-page route showing all paid tiers side by side.
  const upgradeTarget: BillingTier | null = tiersData
    ? tiersData.tiers.find(
        (t) => t.name !== tiersData.default && t.priceIds.length > 0,
      ) ?? null
    : null;
  const upgradePriceId = upgradeTarget?.priceIds[0] ?? "";

  // Stripe webhook → Clerk publicMetadata.tier propagation has up to ~60s
  // lag. Returning from Checkout with ?upgraded=1 means the user expects
  // to see their new tier; force-refresh Clerk's cached user so the UI
  // doesn't keep claiming "free" with the Upgrade button still showing.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (!params.has("upgraded") || !user) return;
    const t = setInterval(() => {
      void user.reload();
    }, 2000);
    const stop = setTimeout(() => clearInterval(t), 30_000);
    return () => {
      clearInterval(t);
      clearTimeout(stop);
    };
  }, [user]);

  async function go(action: BillingAction) {
    setBusy(action);
    setError(null);
    try {
      const token = await getToken();
      if (!token) throw new Error("no session token");

      const here = window.location.origin + window.location.pathname;
      const { url } =
        action === "checkout"
          ? await startCheckout(
              {
                priceId: upgradePriceId,
                successUrl: `${here}?upgraded=1`,
                cancelUrl: here,
              },
              token,
            )
          : await startPortal(here, token);

      window.location.href = url;
    } catch (err) {
      const msg =
        err instanceof ApiCallError
          ? `${err.status} ${err.message}`
          : err instanceof Error
            ? err.message
            : "unknown error";
      setError(msg);
      setBusy(null);
    }
  }

  return (
    <>
      <h1>Your plan</h1>
      <p className="muted">Account: {user?.id}</p>

      <section className="card">
        <div className="row" style={{ justifyContent: "space-between" }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 600 }}>
              Tier: {tier}
            </div>
            <div className="muted">
              {tier === "free"
                ? "Anonymous + signed-in free tier limits apply."
                : "Personal tier — higher rate limits + outbound webhooks."}
            </div>
          </div>
          <div className="row">
            {tier === tiersData?.default && upgradeTarget ? (
              <button
                type="button"
                disabled={busy !== null || upgradePriceId.length === 0}
                onClick={() => go("checkout")}
              >
                {busy === "checkout"
                  ? "…"
                  : `Upgrade to ${capitalize(upgradeTarget.name)}`}
              </button>
            ) : tier !== (tiersData?.default ?? "free") ? (
              <button
                type="button"
                className="secondary"
                disabled={busy !== null}
                onClick={() => go("portal")}
              >
                {busy === "portal" ? "…" : "Manage billing"}
              </button>
            ) : null}
          </div>
        </div>
        {tiersError && (
          <p className="err" style={{ marginTop: 12 }}>
            Couldn't load tier registry: {tiersError}
          </p>
        )}
        {error && <p className="err" style={{ marginTop: 12 }}>{error}</p>}
      </section>

      {/* Signing keys are core identity infrastructure, not a premium
          feature — every registered account can register a signing key
          so the authorship witness leg is available regardless of tier.
          Long-lived API keys (ApiKeysPanel below) remain auth-only;
          tier may shape their usage limits but not their existence. */}
      <KeysSection />

      <ApiKeysPanel />

      <h2>Coming next</h2>
      <p className="muted" style={{ marginTop: 0 }}>
        Built {tiersData ? `against ${tiersData.tiers.length} tier(s) loaded from API` : "—"}.
      </p>
      <ul className="muted">
        <li>Stamps list (calls <code>GET /v1/stamps</code> — pending API endpoint)</li>
        <li>Webhook destinations (<code>/v1/webhooks/*</code> already shipped Block 5)</li>
      </ul>
    </>
  );
}

// Block 6 — account public-key registry surface.
//
// Lists active + revoked keys for the current account, accepts a fresh
// Ed25519 / ES256 PEM via paste, and revokes by kid. Loads on mount; an
// in-component error/success banner replaces toasts (no toast lib in the
// dashboard yet).
function KeysSection() {
  const { getToken } = useAuth();
  const [keys, setKeys] = useState<AccountKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [formKind, setFormKind] = useState<AccountKeyKind>("ed25519");
  const [formPem, setFormPem] = useState("");
  const [formName, setFormName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitErr, setSubmitErr] = useState<string | null>(null);
  const [revoking, setRevoking] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setLoadErr(null);
    try {
      const token = await getToken();
      if (!token) throw new Error("no session token");
      setKeys(await listKeys(token));
    } catch (err) {
      const msg =
        err instanceof ApiCallError
          ? `${err.status} ${err.message}`
          : err instanceof Error
            ? err.message
            : "unknown error";
      setLoadErr(msg);
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => {
    void reload();
  }, [reload]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setSubmitErr(null);
    try {
      const token = await getToken();
      if (!token) throw new Error("no session token");
      await registerKey(
        {
          kind: formKind,
          public_key_pem: formPem.trim(),
          ...(formName.trim() && { name: formName.trim() }),
        },
        token,
      );
      setFormPem("");
      setFormName("");
      setShowForm(false);
      await reload();
    } catch (err) {
      setSubmitErr(
        err instanceof ApiCallError
          ? `${err.status} ${err.message}`
          : err instanceof Error
            ? err.message
            : "unknown error",
      );
    } finally {
      setSubmitting(false);
    }
  }

  async function handleRevoke(kid: string) {
    if (!confirm(`Revoke key ${kid}? Receipts signed after now will fail authorship verification.`)) {
      return;
    }
    setRevoking(kid);
    try {
      const token = await getToken();
      if (!token) throw new Error("no session token");
      await revokeKey(kid, token);
      await reload();
    } catch (err) {
      setLoadErr(
        err instanceof ApiCallError
          ? `${err.status} ${err.message}`
          : err instanceof Error
            ? err.message
            : "revoke failed",
      );
    } finally {
      setRevoking(null);
    }
  }

  return (
    <section className="card" style={{ marginTop: 24 }}>
      <div className="row" style={{ justifyContent: "space-between", marginBottom: 12 }}>
        <h2 style={{ margin: 0 }}>Signing keys</h2>
        <button type="button" onClick={() => setShowForm(!showForm)}>
          {showForm ? "Cancel" : "Register a key"}
        </button>
      </div>
      <p className="muted" style={{ marginTop: 0 }}>
        Public keys discoverable at <code>keys.filevouch.com/{"{account}"}</code>. Receipts
        that include an authorship signature reference these keys by id —
        verifiers resolve the kid against the well-known endpoint.
      </p>

      {showForm && (
        <form onSubmit={handleSubmit} style={{ marginTop: 12 }}>
          <div className="row" style={{ gap: 8 }}>
            <label>
              <span className="muted">Kind</span>
              <select
                value={formKind}
                onChange={(e) => setFormKind(e.target.value as AccountKeyKind)}
                style={{ marginLeft: 6 }}
              >
                <option value="ed25519">ed25519 (EdDSA)</option>
                <option value="es256">es256 (ECDSA P-256)</option>
              </select>
            </label>
            <label style={{ flex: 1 }}>
              <span className="muted">Label (optional)</span>
              <input
                type="text"
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                placeholder="MacBook"
                style={{ marginLeft: 6, width: "100%" }}
              />
            </label>
          </div>
          <label style={{ display: "block", marginTop: 8 }}>
            <span className="muted">Public key (PEM)</span>
            <textarea
              value={formPem}
              onChange={(e) => setFormPem(e.target.value)}
              placeholder="-----BEGIN PUBLIC KEY-----&#10;...&#10;-----END PUBLIC KEY-----"
              rows={8}
              required
              style={{ display: "block", width: "100%", fontFamily: "monospace" }}
            />
          </label>
          {submitErr && <p className="err">{submitErr}</p>}
          <div className="row" style={{ marginTop: 8 }}>
            <button type="submit" disabled={submitting}>
              {submitting ? "Registering…" : "Register"}
            </button>
          </div>
        </form>
      )}

      {loadErr && <p className="err" style={{ marginTop: 12 }}>{loadErr}</p>}

      {loading && !keys.length ? (
        <p className="muted">Loading…</p>
      ) : keys.length === 0 ? (
        <p className="muted">No keys registered yet.</p>
      ) : (
        <table style={{ width: "100%", marginTop: 12, borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ textAlign: "left", borderBottom: "1px solid #eee" }}>
              <th style={{ padding: "6px 4px" }}>Label</th>
              <th style={{ padding: "6px 4px" }}>Kind</th>
              <th style={{ padding: "6px 4px" }}>Kid</th>
              <th style={{ padding: "6px 4px" }}>Status</th>
              <th style={{ padding: "6px 4px" }} />
            </tr>
          </thead>
          <tbody>
            {keys.map((k) => (
              <tr key={k.kid} style={{ borderBottom: "1px solid #f4f4f4" }}>
                <td style={{ padding: "6px 4px" }}>{k.name ?? "—"}</td>
                <td style={{ padding: "6px 4px" }}>{k.kind}</td>
                <td style={{ padding: "6px 4px", fontFamily: "monospace" }}>{k.kid}</td>
                <td style={{ padding: "6px 4px" }}>
                  {k.status === "active" ? (
                    <span>active</span>
                  ) : (
                    <span className="muted">revoked</span>
                  )}
                </td>
                <td style={{ padding: "6px 4px", textAlign: "right" }}>
                  {k.status === "active" && (
                    <button
                      type="button"
                      className="secondary"
                      disabled={revoking !== null}
                      onClick={() => handleRevoke(k.kid)}
                    >
                      {revoking === k.kid ? "…" : "Revoke"}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
